/* eslint-disable no-nested-ternary */
import React, { useState, useLayoutEffect, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import {
  Typography,
  Card,
  Table,
  CardContent,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableFooter,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import { useIntl } from "react-intl";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";

import { IAppState } from "../../../../store/rootDuck";
import { useStyles } from "./hooks/useStyles";
import { TGPTListItem, useGetChats } from "./hooks/useGetChats";
import { useDeleteChat } from "./hooks/useDeleteChat";
import Filter from "./components/Filter";
import Preloader from "../../../../components/ui/Loaders/Preloader";
import { TablePaginator } from "../../../../components/ui/Table/TablePaginator";
import { IUser } from "../../../../interfaces/users";
import AlertDialog from "../../../../components/ui/Dialogs/AlertDialog";
import { useGetMessagesGPT } from "./hooks/useGetMessagesGPT";
import ListMessagesGPT from "./components/ListMessagesGPT";

const ListChats: React.FC = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { fetch, loading, pagination, chats } = useGetChats();
  const { fetchMessagesGPT, loadingGPT, messagesGPT, clearMessages } = useGetMessagesGPT();
  const history = useHistory();
  const { page, perPage, total } = pagination;
  const { me } = useSelector(({ auth: { user } }: IAppState) => ({ me: user }), shallowEqual);
  const [telegramId, setTelegramId] = useState("");
  const [username, setUsername] = useState("");

  const { fetchDelete, successDelete } = useDeleteChat();
  const [userId, setUserId] = useState<TGPTListItem | null>(null);
  const [deleteId, setDeleteId] = useState<string>("");
  const [checkSearch, setCheckSearch] = useState("id");
  const [fio, setFio] = useState({} as IUser);
  const [user, setUser] = useState<IUser | null>(null);
  const [role, setRole] = useState("all");
  const [startDate, setStart] = useState("2025-06-01");
  const [endDate, setEnd] = useState(
    moment(new Date())
      .add(1, "year")
      .format("YYYY-MM-DD")
  );

  const fetchDebouncedId = debounce(() => {
    if (telegramId.trim().length > 0) {
      fetch({
        page: 1,
        per_page: 20,
        date_from: startDate,
        date_to: endDate,
        telegram_id: checkSearch === "id" ? telegramId : undefined,
        username: checkSearch === "name" ? username : undefined,
      });
    }
  }, 2000);

  const fetchDebouncedUsername = debounce(() => {
    if (username.trim().length > 0) {
      fetch({
        page: 1,
        per_page: 20,
        date_from: startDate,
        date_to: endDate,
        telegram_id: checkSearch === "id" ? telegramId : undefined,
        username: checkSearch === "name" ? username : undefined,
      });
    }
  }, 2000);

  useEffect(() => {
    fetchDebouncedId();
    return () => fetchDebouncedId.cancel();
  }, [telegramId]);

  useEffect(() => {
    fetchDebouncedUsername();
    return () => fetchDebouncedUsername.cancel();
  }, [username]);

  useLayoutEffect(() => {
    userId && fetchMessagesGPT(userId.telegram_id);
  }, [userId]);

  useLayoutEffect(() => {
    me && !me?.is_admin && history.replace("/error/error-v1");
  }, [me]);

  useLayoutEffect(() => {
    fetch({
      page: 1,
      per_page: 20,
      date_from: startDate,
      date_to: endDate,
      telegram_id: checkSearch === "id" ? telegramId : undefined,
      username: checkSearch === "name" ? username : undefined,
      // email: checkSearch === "name" ? fio?.email : undefined,
      // phone: checkSearch === "number" ? user?.phone : undefined,
      // role: role === "all" ? undefined : role,
    });
  }, []);

  useLayoutEffect(() => {
    if (successDelete) {
      fetch({
        page: 1,
        per_page: 20,
        date_from: startDate,
        date_to: endDate,
        telegram_id: checkSearch === "id" ? telegramId : undefined,
        username: checkSearch === "name" ? username : undefined,
        // email: checkSearch === "name" ? fio?.email : undefined,
        // phone: checkSearch === "number" ? user?.phone : undefined,
        // role: role === "all" ? undefined : role,
      });
      setUserId(null);
      clearMessages();
    }
  }, [successDelete]);

  if (loading) return <Preloader />;

  return (
    <>
      <Row>
        <Col>
          <div className={classes.container}>
            <Filter
              startDate={startDate}
              setStart={setStart}
              endDate={endDate}
              setEnd={setEnd}
              fetch={fetch}
              checkSearch={checkSearch}
              setCheckSearch={setCheckSearch}
              user={user}
              setUser={setUser}
              fio={fio}
              setFio={setFio}
              role={role}
              setRole={setRole}
              telegramId={telegramId}
              setTelegramId={setTelegramId}
              username={username}
              setUsername={setUsername}
            />

            {loading ? (
              <div className={classes.preloaderContainer}>
                <Preloader />
              </div>
            ) : !chats.length ? (
              <Typography className={classes.empty} component="h5" variant="h5">
                Пусто
              </Typography>
            ) : (
              <Card className={classes.tableContainer}>
                <CardContent className={classes.tableContainerContent}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Telegram ID</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>{intl.formatMessage({ id: "MESSAGES.NUMBER" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "DATE.UPDATE.CHAT" })}</TableCell>
                        <TableCell align="right">{intl.formatMessage({ id: "USER.TABLE.ACTION" })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chats?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {item.telegram_id || "-"}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {item.username || "-"}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {item.messages_num || "-"}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {item.updated_at ? moment(item.updated_at.split("+")[0]).format("YYYY-MM-DD hh:mm") : "-"}
                          </TableCell>
                          <TableCell align="right">
                            <div className={classes.actions}>
                              <IconButton color="secondary" aria-label="previous" onClick={() => item.telegram_id && setUserId(item)}>
                                <VisibilityIcon color="primary" />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                aria-label="previous"
                                onClick={() => item.telegram_id && setDeleteId(item.telegram_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePaginator
                          page={page}
                          realPerPage={chats.length}
                          perPage={perPage}
                          total={total}
                          fetchRows={(page, perPage) =>
                            fetch({
                              page,
                              per_page: perPage,
                              telegram_id: checkSearch === "id" ? telegramId : undefined,
                              username: checkSearch === "name" ? username : undefined,
                              // email: checkSearch === "name" ? fio?.email : undefined,
                              // phone: checkSearch === "number" ? user?.phone : undefined,
                              // role: role === "all" ? undefined : role,
                              date_from: startDate,
                              date_to: endDate,
                            })
                          }
                          label={intl.formatMessage({ id: "CHATS.PER.PAGE" })}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </Col>
      </Row>
      <AlertDialog
        isOpen={!!deleteId}
        text={intl.formatMessage({ id: "CHAT.TITLE.DELETE" })}
        okText={intl.formatMessage({ id: "CATEGORIES.DELETE.OK" })}
        cancelText={intl.formatMessage({ id: "CATEGORIES.DELETE.CANCEL" })}
        handleClose={() => setDeleteId("")}
        handleAgree={() => {
          if (deleteId) {
            fetchDelete(deleteId);
            setDeleteId("");
          }
        }}
      />

      <Dialog
        open={!!userId}
        onClose={() => {
          setUserId(null);
          clearMessages();
        }}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth
        maxWidth="sm"
        // slotProps={{
        //   backdrop: { classes: { root: classes.backdrop } },
        // }}
      >
        <>
          {me && (
            <ListMessagesGPT
              loadingMessages={loadingGPT}
              profileMe={me}
              messagesGPT={messagesGPT}
              isFull
              showUser={userId?.username || "Пользователь"}
              btnDelete={() => userId && setDeleteId(userId.telegram_id)}
            />
          )}
        </>
      </Dialog>
    </>
  );
};

export default ListChats;
